import type { LiveScreenLook, LiveState } from '@models/api';

/*
 * How the big screen looks, saved on the poll so every device shows it. Screen 1 (before Start) takes the host's
 * picture or a colour; Screen 2 (after Start, and the results' join panel) takes a colour only: a picture is for the
 * waiting room, not the poll. Each screen keeps its own colour.
 */

export interface LiveScreenColor {
  id: string;
  label: string;
  background: string;
  textColor: string;
}

/** The frames' peach-to-teal backdrop, the screen before any colour is picked (`#F9AF85` is a token gap, R18). */
export const DAWN: LiveScreenColor = {
  id: 'dawn',
  label: 'Dawn',
  background: 'linear-gradient(180deg, #F9AF85 0%, var(--color-mb-teal) 100%)',
  textColor: '#0D0D0D',
};

/**
 * Dawn first. Blue, Violet, Coral and Forest are new. Coral and Teal start light (Teal's start is `mb-teal`), so their
 * text is ink, as on every teal fill (ui-ux-designer: labels on teal are ink), and each ends where ink still reads: the
 * results' join panel puts text at both ends of its gradient. Ink is at least 4.6:1 across both, white was 2.1 to 3.
 */
export const LIVE_SCREEN_COLORS: LiveScreenColor[] = [
  DAWN,
  {
    id: 'blue',
    label: 'Blue',
    background: 'linear-gradient(135deg, #2F6FED, #12307F)',
    textColor: '#FFFFFF',
  },
  {
    id: 'violet',
    label: 'Violet',
    background: 'linear-gradient(135deg, #7A4DE0, #2A1466)',
    textColor: '#FFFFFF',
  },
  {
    id: 'coral',
    label: 'Coral',
    background: 'linear-gradient(135deg, #FF8A70, #D25E79)',
    textColor: 'var(--color-mb-ink)',
  },
  {
    id: 'forest',
    label: 'Forest',
    background: 'linear-gradient(135deg, #25A36F, #0C4F3B)',
    textColor: '#FFFFFF',
  },
  {
    id: 'teal',
    label: 'Teal',
    background: 'linear-gradient(135deg, var(--color-mb-teal), var(--color-mb-teal-dark))',
    textColor: 'var(--color-mb-ink)',
  },
  {
    id: 'yellow',
    label: 'Yellow',
    background: 'linear-gradient(135deg, #FFCA28, #F9DF8E)',
    textColor: '#0D0D0D',
  },
  {
    id: 'ink',
    label: 'Ink',
    background: 'linear-gradient(135deg, #1A1A1A, #0D0D0D)',
    textColor: '#FFFFFF',
  },
];

export const liveScreenColor = (id: string): LiveScreenColor =>
  LIVE_SCREEN_COLORS.find(color => color.id === id) ?? DAWN;

export const DEFAULT_LIVE_SCREEN_LOOK: LiveScreenLook = {
  image_url: null,
  image_width: null,
  image_height: null,
  use_image: false,
  waiting_color: 'dawn',
  started_color: 'dawn',
  qr_x: 1,
  qr_y: 1,
};

export const lookOf = (state: Pick<LiveState, 'screen_look'>): LiveScreenLook =>
  state.screen_look ?? DEFAULT_LIVE_SCREEN_LOOK;

/** Screen 1 is the only one the picture replaces: before Start, and never once the poll has started. */
export const showsPicture = (
  state: Pick<LiveState, 'live_state' | 'removed' | 'screen_look'>,
): boolean => {
  const look = lookOf(state);
  return (
    !state.removed &&
    state.live_state === 'not_started' &&
    look.use_image &&
    look.image_url !== null
  );
};

/** The screen a projected picture is judged against: Full HD, what most venue projectors take. */
const PROJECTOR = { width: 1920, height: 1080 };
/** Stretched past this, a picture starts to look soft from the room: about anything under 1280×720. */
const MAX_STRETCH = 1.5;

/** A picture is always accepted; this only decides whether the host is warned it may look pixelated. */
export const looksPixelatedOnProjector = (size: { width: number; height: number }): boolean =>
  Math.min(PROJECTOR.width / size.width, PROJECTOR.height / size.height) > MAX_STRETCH;
